// Contact_v02_A2P.tsx
// v02 (A2P compliant updates) – Adds optional phone + explicit SMS opt-in language + conditional consent enforcement.
// v02 – 2026-02-11

import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';

// import NavBar_v03Responsive from './NavBar_v03a_Responsive_component';
import { IAppConfig, IAddress, IOperatingHours } from "../App_v01_Interfaces";

interface AppConfigProps{
  appConfig: IAppConfig;
}

// const Contact: React.FC = () => {

const Contact_v02_A2P: React.FC<AppConfigProps> = ({ appConfig }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ✅ Receipt/Refund CTA (image + click)
  // NOTE: Put the image in: /public/sundancevending-images/receipt-refund.png
  const receiptRefundImageSrc = "/sundance-images/sundance-request-receipt-refund.png";
  const receiptRefund_HAHA_URL = "https://hh.hahavending.com/complaints/home?sticker_num=B131082";
  // NOTE: Update this URL to wherever your receipt/refund flow lives (form, helpdesk, portal, etc.)
  // Using origin keeps it environment-safe for dev/prod.
  const receiptRefundUrl = `${window.location.origin}/support/receipt-refund`;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',                 // ✅ A2P: optional phone capture
    smsOptIn: false,            // ✅ A2P: explicit opt-in
    message: '',
    domainName: window.location.hostname,
    itemCategory: 'Customer',
    itemClass: 'WebForm',
    itemType: 'Feedback',
    smsConsentTimestamp: '',    // ✅ A2P: consent timestamp (ISO)
    smsConsentSourceUrl: window.location.href, // ✅ A2P: source URL
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const { id } = target;

    // ✅ Support checkbox while keeping the same pattern
    const value =
      (target instanceof HTMLInputElement && target.type === 'checkbox')
        ? target.checked
        : target.value;

    setFormData((prevData) => {
      const next: any = {
        ...prevData,
        [id]: value,
      };

      // ✅ If user opts in, capture an ISO timestamp (A2P best practice)
      if (id === 'smsOptIn') {
        next.smsConsentTimestamp = value ? new Date().toISOString() : '';
      }

      return next;
    });
  };

  const isTestMode = false; // ✅ Toggle this to false in production

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    // ✅ A2P: If phone provided, require explicit opt-in (no implicit consent)
    if (formData.phone.trim().length > 0 && formData.smsOptIn !== true) {
      setIsProcessing(false);
      setErrorMessage('To submit a phone number, please confirm SMS consent by checking the opt-in box (or remove the phone number).');
      return;
    }

    if (isTestMode) {
      console.log('🚧 TEST MODE: Skipping API call.');
      console.table({
        domainName: formData.domainName,
        customerName: formData.name,
        emailAddress: formData.email,
        phoneNumber: formData.phone,
        smsOptIn: formData.smsOptIn,
        smsConsentTimestamp: formData.smsConsentTimestamp,
        smsConsentSourceUrl: formData.smsConsentSourceUrl,
        message: formData.message,
        itemCategory: formData.itemCategory,
        itemClass: formData.itemClass,
        itemType: formData.itemType,
      });

      setFormSubmitted(true);
      setErrorMessage('');
      return;
    } //end:isTestMode

    try {
      const response = await fetch(appConfig.contactPageAPIUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          domainName: formData.domainName,
          customerName: formData.name,
          emailAddress: formData.email,

          // ✅ A2P: include phone + consent metadata (backend can ignore if not used)
          phoneNumber: formData.phone,
          smsOptIn: formData.smsOptIn,
          smsConsentTimestamp: formData.smsConsentTimestamp,
          smsConsentSourceUrl: formData.smsConsentSourceUrl,

          message: formData.message,
          itemCategory: formData.itemCategory,
          itemClass: formData.itemClass,
          itemType: formData.itemType,
        }),
      });

      if (!response.ok) {
        setIsProcessing(false);
        throw new Error(`Server responded with ${response.status}`);
      }

      setFormSubmitted(true);
      setIsProcessing(false);
      setErrorMessage('');
    } catch (err: any) {
      setIsProcessing(false);
      setErrorMessage('Cloud Network Error. This form is offline. Please send your message using regular email. Thank you! :)');
      console.error(err);
    }
  };

  return (
    <>
      {/* <NavBar_v03Responsive appConfig={appConfig} /> */}

      <section className="py-5">
        <Container>
          <Row className="mb-4">
            <Col>{appConfig.ContactUsPage.Headline}</Col>
          </Row>

          <Row>
            <Col md={6}>
              {!formSubmitted ? (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3" controlId="name">
                    <Form.Label>Your Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="email">
                    <Form.Label>Email address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  {/* ✅ A2P: Optional phone capture (no consent implied) */}
                  <Form.Group className="mb-3" controlId="phone">
                    <Form.Label>Phone (optional)</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                    <Form.Text className="text-muted">
                      If you provide a phone number, you must explicitly opt in to SMS below (A2P compliance).
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="message">
                    <Form.Label>Your Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="What can we help you with?"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3" controlId="smsOptIn">
                    <div className="d-flex align-items-start">
                      <Form.Check
                        type="checkbox"
                        id="smsOptIn"
                        checked={formData.smsOptIn}
                        onChange={handleChange}
                        required={formData.phone.trim().length > 0}
                        label="I agree to receive text messages regarding my inquiry. Msg & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help."
                      />
                    </div>

                    <Form.Text
                      className="text-muted d-block"
                      style={{ marginLeft: "1.75rem" }}
                    >
                      Providing your phone number does not automatically enroll you in SMS <br></br>
                      — you must also check this box to opt in. ( This language is required by law. )<br></br>
                      <i><b>We will not sell your data, pester you or spam you.</b></i>👍
                    </Form.Text>
                  </Form.Group>

                  <input type="hidden" id="domainName" value={formData.domainName} />
                  <input type="hidden" id="itemCategory" value={formData.itemCategory} />
                  <input type="hidden" id="itemClass" value={formData.itemClass} />
                  <input type="hidden" id="itemType" value={formData.itemType} />

                  {/* ✅ A2P: capture consent context */}
                  <input type="hidden" id="smsConsentTimestamp" value={formData.smsConsentTimestamp} />
                  <input type="hidden" id="smsConsentSourceUrl" value={formData.smsConsentSourceUrl} />

                  <Button variant="primary" type="submit" disabled={isProcessing}>
                    {isProcessing ? 'Processing...' : 'Send Message'}
                  </Button>
                </Form>
              ) : (
                <div className="p-3 rounded border" style={{ backgroundColor: 'var(--bs-success-bg-subtle)' }}>
                  <h5>Thank you! A representative will be following up with you shortly.</h5>
                  <p><strong>Name:</strong> {formData.name}</p>
                  <p><strong>Email:</strong> {formData.email}</p>
                  {formData.phone ? <p><strong>Phone:</strong> {formData.phone}</p> : null}
                  {formData.phone ? <p><strong>SMS Opt-in:</strong> {formData.smsOptIn ? 'Yes' : 'No'}</p> : null}
                  <p><strong>Message:</strong> {formData.message}</p>
                </div>
              )}

              {errorMessage && (
                <Alert variant="danger" className="mt-3">
                  {errorMessage}
                </Alert>
              )}
            </Col>

            <Col md={6}>
         
             {
                appConfig.hideContactAddressInfo === true
                  ? ''
                  : (
                    <>
                      <p><strong>{appConfig.companyLegalName}</strong></p>
                      <p>
                        {appConfig.address.street1}, {appConfig.address.street2}
                        <br />
                        {appConfig.address.city}, {appConfig.address.state} {appConfig.address.zip}
                      </p>
                      <p>Email: {appConfig.infoeMail}</p>

                      <p>Phone: <a href={"tel:" + appConfig.companyMainPhone}>{appConfig.companyMainPhone}</a></p>

                      {appConfig.operatingHours.MF ?
                        <>
                          <h5>Service Hours</h5>
                          <p>
                            {appConfig.operatingHours.MF}
                            <br></br>
                            {appConfig.operatingHours.SA}
                            <br></br>
                            {appConfig.operatingHours.SU}
                          </p>
                        </>
                        : ''}
                    </>
                  )
              }
              </Col>

              {/* <Col md={3}> */}


                            {/* <a href={receiptRefund_HAHA_URL}>
                                      <Card.Img
                                        variant="top"
                                        src={receiptRefundImageSrc}
                                        alt="Request a Receipt or Refund"
                                        style={{ cursor: "pointer" }}
                                      />
                              </a> */}

                          {/* ✅ Receipt/Refund Clickable Image CTA */}
                          {/* <a
                            href={receiptRefundUrl}
                            style={{ textDecoration: "none" }}
                            aria-label="Request a receipt or refund"
                            title="Request a Receipt or Refund"
                          >
                            <Card className="mb-4 shadow-sm">
                              
                             
                              <Card.Body className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div style={{ fontWeight: 700 }}>Receipt / Refund</div>
                                  <div className="text-muted" style={{ fontSize: ".95rem" }}>
                                    Click here to request a receipt or refund.
                                  </div>
                                </div>
                                <Button variant="primary">
                                  Get Started
                                </Button>
                              </Card.Body> 
                            </Card>
                          </a> 
                          
                          */}
                {/* </Col> */}
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Contact_v02_A2P;

/*
File: Contact_v02_A2P.tsx
Description: Contact form with optional phone capture and explicit SMS opt-in language/logic for A2P compliance (v02).
v02 – 2026-02-11
*/